import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCollegeDetails, addTeacherToCollege, deleteUser } from '../services/api';
import {
  FiArrowLeft, FiPlus, FiTrash2, FiUsers, FiUser, FiGlobe,
  FiBook, FiX, FiMail, FiLock, FiAlertCircle
} from 'react-icons/fi';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import NoteCard from '../components/NoteCard';
import EmptyState from '../components/EmptyState';
import LoadingSkeleton from '../components/LoadingSkeleton';
import ConfirmDialog from '../components/ConfirmDialog';

const CollegeDetails = ({ onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const userData = localStorage.getItem('user');
  const currentUser = userData ? JSON.parse(userData) : null;
  const isAdmin = currentUser?.role === 'admin';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('teachers');

  // Add Teacher Modal State
  const [showAddTeacherModal, setShowAddTeacherModal] = useState(false);
  const [teacherFormData, setTeacherFormData] = useState({ name: '', email: '', password: '' });
  const [addTeacherLoading, setAddTeacherLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  // Delete User Confirm State
  const [deletingUser, setDeletingUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getCollegeDetails(id);
      setData(res);
    } catch (err) {
      console.error('Error fetching college details:', err);
      setError(err.response?.data?.message || 'Failed to load college details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const handleAddTeacherSubmit = async (e) => {
    e.preventDefault();
    setAddTeacherLoading(true);
    setModalError('');

    try {
      await addTeacherToCollege(id, teacherFormData);
      setSuccess(`Teacher account "${teacherFormData.name}" added to ${data?.college?.collegeName} successfully!`);
      setShowAddTeacherModal(false);
      setTeacherFormData({ name: '', email: '', password: '' });
      fetchDetails();
    } catch (err) {
      setModalError(err.response?.data?.message || 'Failed to add teacher');
    } finally {
      setAddTeacherLoading(false);
    }
  };

  const handleConfirmDeleteUser = async () => {
    if (!deletingUser) return;
    try {
      setActionLoading(deletingUser._id);
      setError(null);
      setSuccess(null);
      await deleteUser(deletingUser._id);
      setSuccess(`${deletingUser.role.toUpperCase()} account "${deletingUser.name}" deleted successfully.`);
      setDeletingUser(null);
      fetchDetails();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete account');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <Sidebar onLogout={onLogout} />
        <div className="main-content">
          <button className="btn btn-ghost btn-sm mb-3" onClick={() => navigate('/colleges')}>
            <FiArrowLeft /> Back to Colleges
          </button>
          <LoadingSkeleton type="card" count={3} />
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="dashboard-container">
        <Sidebar onLogout={onLogout} />
        <div className="main-content">
          <div className="alert alert-error">
            <FiAlertCircle size={24} />
            <span>Access Denied: Only administrators can access college details.</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="dashboard-container">
        <Sidebar onLogout={onLogout} />
        <div className="main-content">
          <button className="btn btn-ghost btn-sm mb-3" onClick={() => navigate('/colleges')}>
            <FiArrowLeft /> Back to Colleges
          </button>
          <div className="alert alert-error">
            <FiAlertCircle size={20} /> {error || 'College details not found'}
          </div>
        </div>
      </div>
    );
  }

  const { college, stats, students = [], teachers = [], notes = [] } = data;

  return (
    <div className="dashboard-container">
      <Sidebar onLogout={onLogout} />

      <div className="main-content">
        <div className="d-flex justify-between align-center mb-3">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/colleges')}>
            <FiArrowLeft /> Back to Colleges Directory
          </button>
        </div>

        <Header
          title={college.collegeName}
          subtitle={`Institution Code: ${college.collegeCode} • Email Domain: @${college.domain || 'N/A'}`}
        />

        {error && <div className="alert alert-error mb-4">{error}</div>}
        {success && <div className="alert alert-success mb-4">{success}</div>}

        {/* Institution Information Card */}
        <div className="card mb-4">
          <div className="d-flex justify-between align-center" style={{ flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
                {college.collegeName}
              </h2>
              <div className="text-secondary text-sm mt-1" style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <span>Code: <strong>{college.collegeCode}</strong></span>
                <span><FiGlobe style={{ verticalAlign: 'middle' }} /> Allowed Email Domain: <code>@{college.domain || 'N/A'}</code></span>
                <span>Registered: {college.createdAt ? new Date(college.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}</span>
              </div>
            </div>

            {isAdmin && (
              <button
                className="btn btn-primary"
                onClick={() => {
                  setTeacherFormData({ name: '', email: `@${college.domain || ''}`, password: '' });
                  setShowAddTeacherModal(true);
                }}
              >
                <FiPlus /> Add Teacher to College
              </button>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid mb-4">
          <div className="stat-card">
            <div className="stat-icon-wrapper note">
              <FiUser />
            </div>
            <div className="stat-details">
              <h4>Faculty & Teachers</h4>
              <div className="stat-value">{stats.totalTeachers}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon-wrapper subject">
              <FiUsers />
            </div>
            <div className="stat-details">
              <h4>Enrolled Students</h4>
              <div className="stat-value">{stats.totalStudents}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon-wrapper paper">
              <FiBook />
            </div>
            <div className="stat-details">
              <h4>College Notes & Papers</h4>
              <div className="stat-value">{stats.totalNotes}</div>
            </div>
          </div>
        </div>

        {/* Tab Controls */}
        <div className="theme-switcher mb-4" style={{ padding: '4px', width: 'fit-content' }}>
          <button
            type="button"
            className={`theme-option-btn ${activeTab === 'teachers' ? 'active' : ''}`}
            onClick={() => setActiveTab('teachers')}
          >
            <FiUser /> Faculty Teachers ({teachers.length})
          </button>
          <button
            type="button"
            className={`theme-option-btn ${activeTab === 'students' ? 'active' : ''}`}
            onClick={() => setActiveTab('students')}
          >
            <FiUsers /> Students ({students.length})
          </button>
          <button
            type="button"
            className={`theme-option-btn ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            <FiBook /> Study Materials ({notes.length})
          </button>
        </div>

        {/* Tab Content 1: Faculty / Teachers */}
        {activeTab === 'teachers' && (
          <div className="card">
            <div className="d-flex justify-between align-center mb-3">
              <h3 style={{ fontSize: '16px', fontWeight: '600' }}>Faculty Members ({teachers.length})</h3>
              {isAdmin && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    setTeacherFormData({ name: '', email: `@${college.domain || ''}`, password: '' });
                    setShowAddTeacherModal(true);
                  }}
                >
                  <FiPlus /> Add Teacher
                </button>
              )}
            </div>

            {teachers.length === 0 ? (
              <EmptyState
                icon={<FiUser />}
                title="No faculty members added yet"
                description={`Add teacher accounts ending with @${college.domain} to manage notes and requests for this college.`}
                actionButton={
                  isAdmin && (
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        setTeacherFormData({ name: '', email: `@${college.domain || ''}`, password: '' });
                        setShowAddTeacherModal(true);
                      }}
                    >
                      <FiPlus /> Add First Teacher
                    </button>
                  )
                }
              />
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-strong)', background: 'var(--bg-surface-hover)' }}>
                      <th style={{ padding: '12px 16px' }}>Name</th>
                      <th style={{ padding: '12px 16px' }}>Email Address</th>
                      <th style={{ padding: '12px 16px' }}>Role</th>
                      <th style={{ padding: '12px 16px' }}>Date Added</th>
                      {isAdmin && <th style={{ padding: '12px 16px', textAlign: 'right' }}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {teachers.map((t) => (
                      <tr key={t._id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: '12px 16px', fontWeight: '600' }}>{t.name}</td>
                        <td style={{ padding: '12px 16px' }}><code>{t.email}</code></td>
                        <td style={{ padding: '12px 16px' }}>
                          <span className="badge badge-teacher">TEACHER</span>
                        </td>
                        <td style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>
                          {new Date(t.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        {isAdmin && (
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                            <button
                              className="btn btn-sm btn-ghost"
                              style={{ color: 'var(--danger)' }}
                              onClick={() => setDeletingUser(t)}
                              disabled={actionLoading === t._id}
                            >
                              <FiTrash2 /> {actionLoading === t._id ? '...' : 'Remove'}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Tab Content 2: Students */}
        {activeTab === 'students' && (
          <div className="card">
            <h3 className="mb-3" style={{ fontSize: '16px', fontWeight: '600' }}>Enrolled Students ({students.length})</h3>

            {students.length === 0 ? (
              <EmptyState
                icon={<FiUsers />}
                title="No students registered yet"
                description={`Students registering with emails ending in @${college.domain} will automatically appear here.`}
              />
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-strong)', background: 'var(--bg-surface-hover)' }}>
                      <th style={{ padding: '12px 16px' }}>Name</th>
                      <th style={{ padding: '12px 16px' }}>Student Email</th>
                      <th style={{ padding: '12px 16px' }}>Role</th>
                      <th style={{ padding: '12px 16px' }}>Registration Date</th>
                      {isAdmin && <th style={{ padding: '12px 16px', textAlign: 'right' }}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((s) => (
                      <tr key={s._id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: '12px 16px', fontWeight: '600' }}>{s.name}</td>
                        <td style={{ padding: '12px 16px' }}><code>{s.email}</code></td>
                        <td style={{ padding: '12px 16px' }}>
                          <span className="badge badge-student">STUDENT</span>
                        </td>
                        <td style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>
                          {new Date(s.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        {isAdmin && (
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                            <button
                              className="btn btn-sm btn-ghost"
                              style={{ color: 'var(--danger)' }}
                              onClick={() => setDeletingUser(s)}
                              disabled={actionLoading === s._id}
                            >
                              <FiTrash2 /> {actionLoading === s._id ? '...' : 'Delete'}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Tab Content 3: Notes & Study Materials */}
        {activeTab === 'notes' && (
          <div className="card">
            <h3 className="mb-3" style={{ fontSize: '16px', fontWeight: '600' }}>Uploaded Resources ({notes.length})</h3>

            {notes.length === 0 ? (
              <EmptyState
                icon={<FiBook />}
                title="No notes uploaded for this college"
                description="Teachers of this institution can upload notes or approve student note requests."
              />
            ) : (
              <div className="notes-grid">
                {notes.map((note) => (
                  <NoteCard key={note._id} note={note} onDeleteSuccess={fetchDetails} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Teacher Modal */}
      {showAddTeacherModal && (
        <div className="modal-overlay" onClick={() => setShowAddTeacherModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h3>Add Teacher to {college.collegeName}</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowAddTeacherModal(false)}>
                <FiX size={18} />
              </button>
            </div>

            {modalError && <div className="alert alert-error mb-3">{modalError}</div>}

            <form onSubmit={handleAddTeacherSubmit}>
              <div className="form-group">
                <label><FiUser style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Full Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={teacherFormData.name}
                  onChange={(e) => setTeacherFormData({ ...teacherFormData, name: e.target.value })}
                  placeholder="e.g. Dr. Rajesh Sharma"
                  required
                />
              </div>

              <div className="form-group">
                <label><FiMail style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Teacher Email *</label>
                <input
                  type="email"
                  className="form-input"
                  value={teacherFormData.email}
                  onChange={(e) => setTeacherFormData({ ...teacherFormData, email: e.target.value })}
                  placeholder={`teacher@${college.domain}`}
                  required
                />
                <span className="text-secondary text-sm mt-1" style={{ display: 'block' }}>
                  Must end with <code>@{college.domain}</code>
                </span>
              </div>

              <div className="form-group">
                <label><FiLock style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Password *</label>
                <input
                  type="password"
                  className="form-input"
                  value={teacherFormData.password}
                  onChange={(e) => setTeacherFormData({ ...teacherFormData, password: e.target.value })}
                  placeholder="Min. 6 characters"
                  required
                  minLength="6"
                />
              </div>

              <div className="d-flex justify-between gap-3 mt-4">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddTeacherModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={addTeacherLoading}>
                  <FiPlus /> {addTeacherLoading ? 'Adding...' : 'Add Teacher Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Confirm Dialog */}
      <ConfirmDialog
        isOpen={Boolean(deletingUser)}
        title={`Delete ${deletingUser?.role?.toUpperCase()} Account?`}
        message={`Are you sure you want to delete "${deletingUser?.name}" (${deletingUser?.email})? This action cannot be undone.`}
        confirmText="Delete Account"
        onConfirm={handleConfirmDeleteUser}
        onCancel={() => setDeletingUser(null)}
      />
    </div>
  );
};

export default CollegeDetails;
